import { useEffect } from 'react';
import { useRouter } from 'next/router';

import {
  captureFromUrl,
  persistCapturedAttribution
} from '@lib/attribution/cookies';

/**
 * FFH-1160 every-page-load attribution capture.
 *
 * Reads UTMs + click IDs from `window.location.search` on mount and
 * after every client transition, then writes them to the apex-domain
 * first-party cookies. This is the fallback path for visits that
 * bypass the `go.` Cloudflare worker (affiliate links, WhatsApp /
 * Slack URL resolution, Outlook Safe Links) — the worker is still
 * authoritative when it does run, and the first-touch dedup inside
 * `persistCapturedAttribution` keeps both writers compatible.
 *
 * Consent posture: cookies write BEFORE CookieYes returns a decision
 * — matching the `_aid` cookie precedent (now set server-side by
 * ffh-public-api's attribution and funnels routes, likewise
 * pre-consent). The outbound CAPI fanout downstream respects the
 * consent signal via the `consentState` field on the checkout POST;
 * the cookie itself is the attribution payload, not a consent
 * decision.
 *
 * Fires once with the current path on mount, then subscribes to
 * `router.events.on('routeChangeComplete', ...)`. Mount at the top
 * of `HubApp` so it sits OUTSIDE the route-keyed `<ErrorBoundary>`
 * and the subscription survives client navigations.
 */
export const useCaptureAttributionFromUrl = (): void => {
  const router = useRouter();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const capture = (): void => {
      persistCapturedAttribution(captureFromUrl(window.location.search));
    };

    capture();
    router.events.on('routeChangeComplete', capture);
    return () => {
      router.events.off('routeChangeComplete', capture);
    };
  }, [router.events]);
};
